<script setup>
import router from '@/router'
import { getI18n, getTranslatedField } from '@/plugins/i18n'
import { ref, computed, watch, nextTick } from 'vue'
import moment from 'moment'

import { DxResource, DxScheduler } from 'devextreme-vue/scheduler'
import { DxTextBox } from 'devextreme-vue/text-box'
import { DxSelectBox } from 'devextreme-vue/select-box'
import { DxDropDownButton } from 'devextreme-vue'

import { menuRoutesEnum } from '@/router'

import { useGarageStore } from '@/stores/garage.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'

import { codeNameDisplayExpr, customerDisplayExpr } from '@/composables/label'
import { formatDateTime, formatTime } from '@/composables/label'
import { useDialog } from '@/composables/dialog'
import { useDocumentIdentifiers } from '@/composables/document'
import { useGenerateDocumentWarning } from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'
import { useODataSource } from '@/composables/data-grid'

import DocumentGenerateDialog from '@/components/shared/DocumentGenerateDialog.vue'
import EntityLockAvatarTemplate from '@/components/shared/EntityLockAvatarTemplate.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import GenericPopup from '@/components/shared/GenericPopup.vue'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import GarageEventSchedulerForm from '@/components/garage/GarageEventSchedulerForm.vue'

import { useEntityLock } from '@/services/entity-lock.service'

const i18n = getI18n()
const garageStore = useGarageStore()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const { notifyWarning } = useNotify()
const { addWarning: addDocumentWarning, displayWarnings: displayGenerateDocumentWarnings, checkPartialFields } = useGenerateDocumentWarning()
const { checkAndAskForUpdateCustomerEmailOrMobile, checkCustomerPrivacyAsync } = useDocumentIdentifiers()

const { genericDialog } = useDialog()

const entityLock = useEntityLock({
  baseLockUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.eventsUrl,
  modelName: 'GarageEvent',
  gridRef: ref(null),
})

const calendarViews = [
    {
      name: i18n.t('common.scheduler.hourlyTimeline'),
      type: 'timelineWeek',
      groups: ['mechanics'],
      startDate: new Date(),
      maxAppointmentsPerCell: 'unlimited',
    },
    {
      name: i18n.t('common.scheduler.dailyView'),
      type: 'day',
      groups: ['mechanics'],
      intervalCount: registryStore.companyData.default_scheduler_daily_view_days,
      startDate: new Date(),
      maxAppointmentsPerCell: 'unlimited',
    },
    {
      name: i18n.t('common.scheduler.weeklyView'),
      type: 'week',
      intervalCount: 1,
      startDate: new Date(),
      maxAppointmentsPerCell: 'unlimited',
    },
    {
      name: i18n.t('common.scheduler.monthlyView'),
      type: 'month',
      groups: ['mechanics'],
      startDate: new Date(),
      maxAppointmentsPerCell: 'unlimited',
    },
]

const currentAppointment = ref({})
const showModal = ref(false)
const currentDate = ref(new Date().toISOString())
const scheduler = ref(null)
const refDocument = ref(null)
const generateDialogVisible = ref(false)
const generateDocumentType = ref(null)

function makeLegendGroup(label, list, valuePrefix = null) {
  return [
    { key: label, value: valuePrefix ? `${i18n.t(valuePrefix, 2)} ${label.toLowerCase()}` : label, isLabel: true },
    ...list.map(i => ({ key: i.code, color: i.color, value: getTranslatedField(i) })),
  ]
}

const dataLegend = computed(() => {
  const legend = [...makeLegendGroup(`${i18n.t('common.status', 2)} ${i18n.t('common.documentProgressStatus').toLowerCase()}`, garageStore.documentProgressStatuses)]
  if (webSiteStore.defaultAgendaColoredBy === webSiteStore.agendaColoredByEnum.INTERVENTION_TYPE) {
    legend.push(...makeLegendGroup(i18n.t('common.interventionType'), garageStore.interventionTypes))
  } else {
    const mechanics = webSiteStore.userDetails.filter(i => i.is_mechanic).map(i => ({ code: i.id, color: i.color, name: `${i.name_ext} ${i.name}` }))
    legend.push(...makeLegendGroup(i18n.t('common.mechanic', 2), mechanics))
  }
  return legend
})

const schedulerForm = ref(null)

const normalizeMechanics = m => Array.isArray(m) ? m : m ? [m] : []

const normalizeAppointment = (appointment) => {
  const normalizedAppointment = structuredClone(appointment ?? {})
  normalizedAppointment.mechanics = normalizeMechanics(normalizedAppointment.mechanics)
  return normalizedAppointment
}

const isGenerateDisabled = computed(() => {
  return !currentAppointment.value || !currentAppointment.value.id || currentAppointment.value?.worksheets?.length > 0 || isEntityLocked(currentAppointment.value)
})

const hasGeneratedDocument = (appointment) => appointment?.worksheets?.length > 0
const isAppointmentReadOnly = (appointment) => hasGeneratedDocument(appointment) || isEntityLocked(appointment)

const dataSource = computed(() => {
  let filter = null

  if (searchTextFilter.value && searchTextFilter.value.length > 0) {
    filter = ['search_field', 'contains', searchTextFilter.value]
  }
  if (mechanicFilter.value) {
    filter = filter ? [filter, 'and', ['mechanics', '=', mechanicFilter.value]] : ['mechanics', '=', mechanicFilter.value]
  }
  if (interventionTypeFilter.value) {
    filter = filter ? [filter, 'and', ['intervention_type_ref', '=', interventionTypeFilter.value]] : ['intervention_type_ref', '=', interventionTypeFilter.value]
  }

  return useODataSource(garageStore.api.getAbsoluteUrl() + garageStore.api.eventsUrl, {
    filter: filter,
    pageSize: null,
    customParamsFn: () => { return { $notop: true } }
  })
})

const updateDataSource = async() => { await scheduler.value.instance.getDataSource().reload() }

const currentViewIndex = ref(2) // Default to weekly view
const currentViewName = computed(() => calendarViews[currentViewIndex.value].name)
const onOptionChanged = e => {
  if (e.name === 'currentView') {
    currentViewIndex.value = calendarViews.findIndex(i => i.name === e.value)
    if (currentViewIndex.value === 0) {
      nextTick(() => scrollToCurrentDay())
    }
  }
}

const scrollToCurrentDay = () => {
  const instance = scheduler.value?.instance
  if (!instance?.scrollTo) return
  const targetDate = currentDate.value instanceof Date ? currentDate.value : new Date(currentDate.value)
  instance.scrollTo(targetDate)
}

const filterChanged = ref(false)

const searchTextFilter = ref('')
const searchTextInput = ref('')
let searchTimeout = null

const onSearchInput = (e) => {
  filterChanged.value = true
  searchTextInput.value = e.value

  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(() => {
    searchTextFilter.value = searchTextInput.value
  }, 500)
}

const mechanics = webSiteStore.userDetails.filter(i => i.is_mechanic).map(i => ({
  full_name: `${i.name_ext} ${i.name}`,
  ...i
}))
const mechanicFilter = ref(null)
const onMechanicFilterChanged = e => {
  mechanicFilter.value = e.value
  filterChanged.value = true
}

const interventionTypes = garageStore.getLocalizedInterventionTypes()
const interventionTypeDisplayExpr = i => codeNameDisplayExpr(i)
const interventionTypeFilter = ref(null)
const onInterventionTypeFilterChanged = e => {
  interventionTypeFilter.value = e.value
  filterChanged.value = true
}

const onClearFilter = () => {
  mechanicFilter.value = null
  interventionTypeFilter.value = null
  searchTextInput.value = searchTextFilter.value = ''
  filterChanged.value = false
}

const isDateAllowed = (date) => {
  const now = new Date()
  now.setSeconds(0, 0)

  if (now.getMinutes() < 30) { now.setMinutes(0) } else { now.setMinutes(30) }

  return new Date(date) >= now
}

const isEntityLocked = (appointment) => {
  return appointment?.id && entityLock.isLocked(false, appointment.id) && !entityLock.isLocked(true, appointment.id)
}

const onAppointmentFormOpening = (e, asReadOnly = false) => {
  e.cancel = true
  const readOnly = asReadOnly || isAppointmentReadOnly(e.appointmentData)
  if (!readOnly && e.appointmentData.id) {
    entityLock.lockEntity(e.appointmentData.id)
  }
  scheduler.value.instance.hideAppointmentTooltip()
  if (!e.appointmentData.id && !isDateAllowed(e.appointmentData?.start)) {
    return notifyWarning(i18n.t('warning.only_future_appointments_are_allowed'))
  }

  currentAppointment.value = normalizeAppointment(e.appointmentData)
  showModal.value = true
}

const onAppointmentUpdating = (e) => {
  if (hasGeneratedDocument(e.oldData)) { e.cancel = true }
}

const onAppointmentRendered = (e) => {
  const el = e.appointmentElement
  const currentWidth = parseFloat(el.style.width)
  if (currentWidth) {
    el.style.setProperty('width', `${currentWidth * 0.9}px`, 'important')
  }
}

const handleAppointmentClick = (e) => {
  currentAppointment.value = normalizeAppointment(e.appointmentData)
}

const handleCellClick = (e) => {
  currentAppointment.value = null
}

const confirmAppointmentDeletion = (appointmentData) => {
  if (appointmentData?.worksheets?.length > 0) {
    notifyWarning(i18n.t('warning.cannot_delete_appointments_with_documents'))
    return
  }

  genericDialog(
    null,
    i18n.t('common.confirmDelete'),
    { cancel: true, persistent: true }
  ).onOk(async() => {
    await garageStore.api.deleteEventAsync(appointmentData?.id)
    await updateDataSource()
  })
}

const onAppointmentDeleting = (data) => {
  data.cancel = true
  confirmAppointmentDeletion(data.appointmentData)
}

const onFormSave = async(e) => {
  const data = await schedulerForm.value?.onSave()
  if (!data) return

  if ('deposit' in data) data.deposit = data.deposit || registryStore.companyData.deposit
  if ('customer' in data) delete data.customer.address
  if (!('vehicle_id' in data)) delete data.vehicle
  if ('vehicle_id' in data) data.vehicle = data.vehicle_id
  if ('customer_id' in data) data.customer = { ...data.customer, id: data.customer_id }

  let res = null
  if (!data.id) {
    res = await garageStore.api.createEventAsync(data)
  } else {
    res = await garageStore.api.updateEventAsync(data.id, data)
  }
  if (res) {
    await entityLock.releaseLock(data.id)
    showModal.value = false
    checkAndAskForUpdateCustomerEmailOrMobile(data)
    await checkCustomerPrivacyAsync(data.customer_id)
    await updateDataSource()
  }
}

const onFormCancel = async(e) => {
  await entityLock.releaseLock(currentAppointment.value.id)
  currentAppointment.value = null
  showModal.value = false
}

const generateDocumentItems = [
  { id: portalStore.documentGenerateEnum.WORKSHEET, text: i18n.t('common.worksheet'), icon: 'mdi mdi-wrench' }
]
const generateDocumentOptions = {
  width: '20rem'
}

const onGenerateDocumentItemClick = async(e) => {
  if (currentAppointment.value) {
    generateDocumentType.value = e.itemData.id

    const appointmentName = getAppointmentText(currentAppointment.value)
    await checkPartialFields(generateDocumentType.value, appointmentName, null, addDocumentWarning)

    if (!displayGenerateDocumentWarnings()) {
      refDocument.value = structuredClone(currentAppointment.value)
      refDocument.value.generating_document_date = refDocument.value?.start
      refDocument.value.date = moment(new Date()).format('YYYY-MM-DD')
      refDocument.value.worksheet_number_mnemonic = ''
      refDocument.value.paid_amount = 0
      refDocument.value.currency_ref = webSiteStore.currencies.find(c => c.id === registryStore.companyData.default_currency_ref)?.id
      refDocument.value.garage_id = registryStore.companyData.garage
      refDocument.value.series = new Date(currentAppointment.value.start)?.getFullYear().toString()

      const customer = registryStore.customers.find(c => c.id === currentAppointment.value?.customer_id)
      refDocument.value.customer_name_name_ext_vat = customer ? `${customer.name} ${customer.name_ext ? customer.name_ext : ''} ${customer.vat_number ? '- ' + customer.vat_number : ''}` : ''
      generateDialogVisible.value = true
    }
  }
}

const onDocumentGenerated = (e) => {
  currentAppointment.value = refDocument.value = null
  dataSource.value.load()
}

watch(() => webSiteStore.sideBarExpanded, (value, oldValue) => {
  if (value !== oldValue) {
    setTimeout(() => scheduler.value.instance.repaint(), 200)
  }
})

const getAppointmentText = (data) => {
  const customer = registryStore.customers.find(c => c.id === data.customer_id)
  const vehicle = registryStore.vehicles.find(v => v.id === data.vehicle_id)
  return `${customer.name} - ${vehicle.name}`
}

const getAppointmentCaption = (data) => {
  return `${data.start ? formatTime(data.start, portalStore.selectedLanguage) : ''} - ${formatTime(data.end, portalStore.selectedLanguage)}`
}

const getAppointmentStyle = (data) => {
  const style = {}
  let statusColor = data.appointmentData?.worksheets[0]?.document_progress_status_color
  if (!statusColor) {
    const statuses = garageStore.getLocalizedDocumentProgressStatuses()
    statusColor = statuses.filter(s => s.type === portalStore.documentProgressStatusesEnum.PLANNED && s.visible).sort((a, b) => b.order - a.order)[0]?.color
  }
  style.borderLeftColor = statusColor
  style.borderLeftWidth = '0.5rem'
  style.borderLeftStyle = 'solid'
  style.padding = '0.5rem'

  return style
}
// --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
// const getProcessesDescription = (appointment) => {
//   const descriptions = appointment?.processes?.map(item => item.product_description)

//   const result = descriptions.join(', ')
//   return descriptions.length <= 3 ? result : result + '...'
// }

const extractReferenceData = (appointment) => {
  const vehicle = registryStore.vehicles.find(v => v.id === appointment.vehicle_id)
  const courtesyVehicle = registryStore.vehicles.find(v => v.id === appointment.courtesy_vehicle)

  const dayDiff = moment(appointment.end).day() !== moment(appointment.start).day()

  const data = [
    { key: i18n.t('common.customer'), value: customerDisplayExpr({...registryStore.customers.find(i => i.id === appointment.customer_id), mobile: appointment.customer.mobile}, true) },
    { key: i18n.t('common.startDate'), value: dayDiff ? formatDateTime(appointment.start, portalStore.selectedLanguage) : formatTime(appointment.start, portalStore.selectedLanguage) },
    { key: i18n.t('common.endDate'), value: dayDiff ? formatDateTime(appointment.end, portalStore.selectedLanguage) : formatTime(appointment.end, portalStore.selectedLanguage) },
    { key: i18n.t('common.supplierBrand'), value: codeNameDisplayExpr(registryStore.getSupplierBrandById(vehicle?.supplier_brand_ref)) },
    { key: i18n.t('common.vehicleInfo'), value: `${vehicle?.plate_number} - ${vehicle?.vin} - ${vehicle?.km}` },
    { key: i18n.t('common.customerNotes'), value: appointment.customer_notes },
    // { key: i18n.t('common.processes'), value: getProcessesDescription(appointment) },  --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
    { key: i18n.t('common.courtesyVehicle'), value: courtesyVehicle?.name },
  ]

  return data
}

const printGarageEvent = async(data) => {
  const filename = i18n.t('common.appointment') + '.pdf'
  await garageStore.api.printGarageEventsAsync(filename, {filename: filename, ids: [data.appointmentData.id], timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
}

const currentDeposit = computed(() => {
  const depositId = currentAppointment.value?.deposit ?? registryStore.companyData.deposit
  if (!!depositId && currentAppointment.value) {
    currentAppointment.value.deposit = depositId
  }
  return registryStore.getLocalizedDeposits().find(d => d.id === depositId)
})

const getFormTitle = () => {
  const code = currentDeposit.value?.code ?? ''
  const name = currentDeposit.value?.name ?? ''

  return `${i18n.t('common.appointment')} | ${code} ${name}`
}

const onCompanyDataClick = (tab) => {
  const target = menuRoutesEnum.COMPANY
  const route = { path: target, query: { tab } }
  setTimeout(() => router.push(route), 500)
}

</script>
<template>
  <div>
    <document-generate-dialog
      v-if="generateDialogVisible"
      v-model:visible="generateDialogVisible"
      :generate-document-type="generateDocumentType"
      :generating-document-type="portalStore.documentGeneratingEnum.SCHEDULER"
      :generate-function="garageStore.api.generateWorksheetFromSchedulerAsync"
      :next-id-function="() => garageStore.api.getWorksheetNextIdByDataAsync()"
      :selected-item="refDocument"
      @created="onDocumentGenerated"
      @cancel="refDocument = null"
    />

    <div :style="{ overflow: 'hidden' }">
      <div class="header flex items-center">
        <dx-drop-down-button
          class="generate-document-button"
          icon="textdocument"
          :text="`${i18n.t('common.generate')}...`"
          :use-select-mode="false"
          :disabled="isGenerateDisabled"
          display-expr="text"
          :drop-down-options="generateDocumentOptions"
          :items="generateDocumentItems"
          @item-click="onGenerateDocumentItemClick"
        />
        <div
          style="display: flex; align-items: center; margin-right: 1rem;"
          class="q-ml-md"
        >
          <dx-select-box
            v-model="webSiteStore.defaultAgendaColoredBy"
            :items="[
              { text: i18n.t('common.scheduler.eventColorByType'), value: webSiteStore.agendaColoredByEnum.INTERVENTION_TYPE },
              { text: i18n.t('common.scheduler.eventColorByMechanic'), value: webSiteStore.agendaColoredByEnum.MECHANIC },
            ]"
            display-expr="text"
            value-expr="value"
            style="min-width: 180px;"
          />
        </div>
        <dx-select-box
          v-model="interventionTypeFilter"
          class="q-ml-md"
          :data-source="interventionTypes"
          :display-expr="interventionTypeDisplayExpr"
          value-expr="id"
          :show-clear-button="true"
          @value-changed="onInterventionTypeFilterChanged"
        />
        <dx-text-box
          v-model="searchTextInput"
          class="q-ml-md"
          value-change-event="input"
          :placeholder="i18n.t('common.searchDots')"
          width="15rem"
          @value-changed="onSearchInput"
        />
        <dx-select-box
          v-model="mechanicFilter"
          class="q-ml-md"
          :data-source="mechanics"
          display-expr="full_name"
          value-expr="id"
          :show-clear-button="true"
          @value-changed="onMechanicFilterChanged"
        />
        <q-btn
          class="q-ml-md"
          icon="mdi-filter-off"
          size=".7rem"
          :disabled="!filterChanged"
          round
          flat
          @click="onClearFilter"
        />
        <q-btn
          class="q-ml-md"
          icon="mdi-cog"
          size=".7rem"
          round
          flat
          @click="onCompanyDataClick(1)"
        />
        <q-btn
          class="q-ml-md"
          icon="mdi-palette"
          size=".7rem"
          round
          flat
          @click="onCompanyDataClick(3)"
        >
          <information-tool-tip
            :data="dataLegend"
            :delay="500"
            :options="{ anchor: 'bottom left', showMobileInfo: false, showColor: true, minWidth: '15rem', noKey: true }"
          />
        </q-btn>
      </div>
      <dx-scheduler
        id="scheduler"
        ref="scheduler"
        :class="{'time-line': currentViewIndex === 0}"
        :show-current-time-indicator="true"
        :shade-until-current-time="true"
        all-day-panel-mode="hidden"
        start-date-expr="start"
        end-date-expr="end"
        text-expr="description"
        key-expr="id"
        height="100%"
        :start-day-hour="registryStore.companyData.default_scheduler_start_hour"
        :end-day-hour="registryStore.companyData.default_scheduler_end_hour"
        :remote-filtering="true"
        :data-source="dataSource"
        :current-date="currentDate"
        :views="calendarViews"
        :current-view="currentViewName"
        :first-day-of-week="registryStore.companyData.default_scheduler_first_day"
        :editing="{
          allowAdding: true,
          allowDeleting: true,
          allowUpdating: true,
          allowDragging: true,
        }"
        :on-appointment-deleting="onAppointmentDeleting"
        :on-appointment-form-opening="onAppointmentFormOpening"
        :on-appointment-updating="onAppointmentUpdating"
        :on-appointment-click="handleAppointmentClick"
        :on-appointment-dbl-click="onAppointmentFormOpening"
        :on-appointment-rendered="onAppointmentRendered"
        :on-cell-click="handleCellClick"
        @option-changed="onOptionChanged"
      >
        <template #appointmentTooltipTemplate="{ data }">
          <div
            style="padding: 0.75rem; cursor: default;"
            @click.stop
          >
            <div class="flex items-start">
              <div
                class="appointment-text q-mb-md q-mt-xs"
                style="flex-grow: 1;"
              >
                <div class="appointment-title">
                  {{ getAppointmentText(data.appointmentData) }}
                </div>
                <div
                  class="appointment-time"
                  style="display: flex; align-items: center; gap: 0.5rem;"
                >
                  {{ getAppointmentCaption(data.appointmentData) }}
                  <reference-link
                    v-if="data.appointmentData.worksheets?.length > 0"
                    :data="{
                      prefix: data.appointmentData.key,
                      series: data.appointmentData.worksheets[0].series,
                      number: data.appointmentData.worksheets[0].number_mnemonic
                    }"
                    :text-fields="['prefix', 'series', 'number']"
                    :query-fields="['series', 'number']"
                    :target="menuRoutesEnum.WORKSHEETS"
                    has-underline
                  />
                </div>
              </div>

              <div class="flex items-center tooltip-actions">
                <div class="q-mr-xs">
                  <q-btn
                    round
                    flat
                    icon="mdi-printer"
                    size="sm"
                    @click.stop="() => { scheduler.instance.hideAppointmentTooltip(); printGarageEvent(data); }"
                  />
                </div>
                <div class="q-mr-xs">
                  <q-btn
                    v-if="isAppointmentReadOnly(data.appointmentData)"
                    round
                    flat
                    icon="mdi-magnify"
                    size="sm"
                    @click.stop="() => { scheduler.instance.hideAppointmentTooltip(); onAppointmentFormOpening(data, true); }"
                  />
                  <q-btn
                    v-else
                    round
                    flat
                    icon="mdi-pencil"
                    size="sm"
                    @click.stop="() => { scheduler.instance.hideAppointmentTooltip(); onAppointmentFormOpening(data); }"
                  />
                </div>
                <div @click.stop>
                  <entity-lock-avatar-template
                    v-if="isEntityLocked(data.appointmentData)"
                    class="dx-link dx-link-icon"
                    :entity-name="'GarageEvent'"
                    :entity-id="data.appointmentData.id"
                    :size="'16px'"
                    @click="entityLock.unlockRequestConfirmation(data.appointmentData.id)"
                  />
                  <q-btn
                    v-else-if="!hasGeneratedDocument(data.appointmentData)"
                    round
                    flat
                    icon="mdi-delete"
                    size="sm"
                    @click.stop="() => { scheduler.instance.hideAppointmentTooltip(); confirmAppointmentDeletion(data.appointmentData); }"
                  />
                </div>
              </div>
            </div>

            <q-markup-table
              flat
              dense
            >
              <tbody>
                <tr
                  v-for="(item, index) in extractReferenceData(data.appointmentData)"
                  :key="index"
                  class="q-tr--no-hover"
                >
                  <td class="text-left table-cell">
                    {{ item.key }}
                  </td>
                  <td class="text-right text-bold table-cell">
                    <div class="flex items-center justify-end">
                      <span>{{ item.value }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
          </div>
        </template>
        <template #appointmentTemplate="{ data }">
          <div
            :class="`dx-scheduler-appointment-status-type-${data.appointmentData.worksheets[0]?.document_progress_status_type}`"
            :style="getAppointmentStyle(data)"
          >
            <div style="display: flex; justify-content: space-between;">
              <div style="font-size: 0.9rem;">
                {{ getAppointmentText(data.appointmentData) }}
              </div>
            </div>
            <div style="font-size: 0.8rem;">
              {{ getAppointmentCaption(data.appointmentData) }}
            </div>
            <div class="flex items-center q-gutter-sm">
              <q-icon
                v-if="!!data.appointmentData.on_site_waiting"
                name="mdi-account-clock"
                class="custom-icon"
              />
              <q-icon
                v-if="!!data.appointmentData.courtesy_vehicle"
                name="mdi-motorbike"
                class="custom-icon"
              />
            </div>
            <reference-link
              v-if="data.appointmentData?.worksheets?.length > 0"
              class="q-mt-md"
              :data="{
                prefix: i18n.t('common.worksheetAlt'),
                series: data.appointmentData?.worksheets[0]?.series,
                number: data.appointmentData?.worksheets[0]?.number_mnemonic
              }"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number']"
              :target="menuRoutesEnum.WORKSHEETS"
              color="white"
              has-underline
            />
          </div>
        </template>

        <dx-resource
          :use-color-as-default="webSiteStore.defaultAgendaColoredBy === webSiteStore.agendaColoredByEnum.MECHANIC"
          :data-source="mechanics"
          :allow-multiple="true"
          label="Mechanic"
          field-expr="mechanics"
          display-expr="full_name"
        />
        <dx-resource
          :data-source="garageStore.interventionTypes"
          label="intervention_type_ref"
          field-expr="intervention_type_ref"
        />
      </dx-scheduler>

      <generic-popup
        v-if="showModal"
        v-model:visible="showModal"
        :options="{ item: currentAppointment, title: getFormTitle(), width: '100rem', height: '70vh', saveButton: { disabled: isAppointmentReadOnly(currentAppointment), hide: isAppointmentReadOnly(currentAppointment) } }"
        @save="onFormSave"
        @cancel="onFormCancel"
      >
        <garage-event-scheduler-form
          ref="schedulerForm"
          :data="currentAppointment"
          :options="{ readOnly: isAppointmentReadOnly(currentAppointment) }"
        />
      </generic-popup>
    </div>
  </div>
</template>
<style scoped>
  .header {
    display: flex;
    justify-content: end;
    margin-right: 1rem;
  }

  #scheduler {
    border: none;
    margin-top: 1rem;
  }

  .time-line {
    & :deep(.dx-scheduler-cell-sizes-horizontal) {
      width: 4rem;
    }
  }

  :deep(.generate-document-button .dx-widget.dx-button) {
    border: 0;
  }

  .custom-icon {
    font-size: 1.2rem;
  }

  .appointment-text {
    text-align: left;
  }

  .appointment-title {
    font-size: 1rem;
    font-weight: 600;
  }

  .appointment-time {
    font-size: 0.7rem;
    color: #6b7280;
  }

  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-item.dx-list-item) {
    animation: none !important;
    transform: none !important;
    transition-property: none !important;
    width: 30rem !important;
  }

  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-list-item > .dx-inkripple > .dx-inkripple-wave) {
    animation: none !important;
    transform: none !important;
    transition-property: none !important;
  }

  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-list:not(.dx-list-select-decorator-enabled) .dx-list-item.dx-state-active .dx-list-slide-item-content) {
    background-color: initial;
  }

  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-list-item.dx-state-active),
  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-list-item.dx-state-focused),
  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-list-item.dx-state-hover) {
    background-color: initial !important;
    color: initial !important;
  }

  :global(.dx-scheduler-appointment-tooltip-wrapper .dx-overlay-content .dx-popup-content),
  :global(.dx-scheduler-appointment-tooltip-wrapper > div.dx-overlay-content) {
    max-height: 30rem !important;
    min-width: 30rem !important;
    height: auto;
    width: auto;
  }

  :deep(.table-cell) {
    padding: 0 !important;
  }
</style>
